import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { showSuccessToast, showErrorToast } from '../../utils/notificationUtils';
import { useTranslation } from 'react-i18next';

const AttachmentsModal = ({ isOpen, onClose, outpatientId, inpatientId, onSave, readOnly = false }) => {
  const [attachments, setAttachments] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]); // Local previews before upload
  const [loading, setLoading] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [errors, setErrors] = useState({});
  const fileInputRef = useRef(null);
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const maxFileSizeMB = 10;

  // Load existing attachments when modal opens
  useEffect(() => {
    if (isOpen && (outpatientId || inpatientId)) {
      loadExistingAttachments();
    }
  }, [isOpen, outpatientId, inpatientId]);

  // Handle keyboard events and body scroll
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const loadExistingAttachments = async () => {
    if (!outpatientId && !inpatientId) return;
    const endpoint = outpatientId ? `/api/v1/outpatient-visits/${outpatientId}/attachments` : `/api/v1/inpatient-visits/${inpatientId}/attachments`;
    setLoadingExisting(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}${endpoint}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });

      if (response.ok) {
        const result = await response.json();
        if (result.status === 'success' && result.data) {
          // Handle both array and object responses
          const attachmentsData = Array.isArray(result.data) ? result.data : (result.data.attachments || result.data.media || []);
          setAttachments(attachmentsData);
        }
      }
    } catch (error) {
      console.error('Error loading existing attachments:', error);
    } finally {
      setLoadingExisting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && !loading && !loadingExisting && !uploading) {
      handleClose();
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFiles = (files) => {
    const fileArray = Array.from(files);
    const validFiles = fileArray.filter(file => {
      const maxSize = maxFileSizeMB * 1024 * 1024; // 10MB
      const allowedTypes = [
        'image/jpeg', 'image/png', 'image/gif', 'image/webp',
        'video/mp4', 'video/webm', 'video/ogg',
        'audio/mpeg', 'audio/mp3', 'audio/wav',
        'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/plain', 'text/csv'
      ];

      if (file.size > maxSize) {
        showErrorToast(t('receptionOutpatients.attachments.toasts.fileTooLarge', { name: file.name, maxSize: maxFileSizeMB }));
        return false;
      }

      if (!allowedTypes.includes(file.type)) {
        showErrorToast(t('receptionOutpatients.attachments.toasts.fileUnsupported', { type: file.type || t('receptionOutpatients.attachments.metadata.unknown') }));
        return false;
      }

      return true;
    });

    if (validFiles.length > 0) {
      // Append to pending queue with preview URLs
      const toAdd = validFiles.map((file) => ({
        file,
        previewUrl: URL.createObjectURL(file),
        kind: file.type.startsWith('image/') ? 'image'
          : file.type.startsWith('video/') ? 'video'
            : file.type === 'application/pdf' ? 'pdf'
              : file.type.startsWith('audio/') ? 'audio'
                : 'document'
      }));
      setPendingFiles(prev => [...prev, ...toAdd]);
    }
  };

  const uploadFiles = async (files) => {
    setUploading(true);
    try {
      const endpoint = outpatientId ? `/api/v1/outpatient-visits/${outpatientId}/upload-media` : `/api/v1/inpatient-visits/${inpatientId}/upload-media`;
      const formData = new FormData();
      // API expects media[] multiple entries
      files.forEach((item) => {
        const f = item.file ? item.file : item; // support both File and queued item
        formData.append('media[]', f);
      });

      const response = await fetch(`${import.meta.env.VITE_API_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        if (result.status === 'success') {
          showSuccessToast(t('receptionOutpatients.attachments.toasts.uploadSuccess', { count: files.length }));
          loadExistingAttachments(); // Refresh the list
          onSave && onSave(result.data);
          // Clear pending previews
          pendingFiles.forEach(p => p.previewUrl && URL.revokeObjectURL(p.previewUrl));
          setPendingFiles([]);
        } else {
          showErrorToast(result.message || t('receptionOutpatients.attachments.toasts.uploadFailed'));
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        showErrorToast(errorData.message || t('receptionOutpatients.attachments.toasts.uploadFailed'));
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      showErrorToast(t('receptionOutpatients.attachments.toasts.uploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const removePendingFile = (indexToRemove) => {
    setPendingFiles(prev => {
      const copy = [...prev];
      const item = copy[indexToRemove];
      if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
      copy.splice(indexToRemove, 1);
      return copy;
    });
  };

  const deleteAttachment = async (attachmentId) => {
    setLoading(true);
    try {
      const endpoint = outpatientId ? `/api/v1/outpatient-visits/${outpatientId}/delete-media/${attachmentId}` : `/api/v1/inpatient-visits/${inpatientId}/delete-media/${attachmentId}`;
      const response = await fetch(`${import.meta.env.VITE_API_URL}${endpoint}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        showSuccessToast(t('receptionOutpatients.attachments.toasts.deleteSuccess'));
        loadExistingAttachments(); // Refresh the list
      } else {
        showErrorToast(t('receptionOutpatients.attachments.toasts.deleteFailed'));
      }
    } catch (error) {
      console.error('Error deleting attachment:', error);
      showErrorToast(t('receptionOutpatients.attachments.toasts.deleteFailed'));
    } finally {
      setLoading(false);
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType.startsWith('image/')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <polyline points="21,15 16,10 5,21" />
        </svg>
      );
    } else if (fileType === 'application/pdf') {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14,2 14,8 20,8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <polyline points="10,9 9,9 8,9" />
        </svg>
      );
    } else {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14,2 14,8 20,8" />
        </svg>
      );
    }
  };

  const getAttachmentKind = (fileType) => {
    if (!fileType) return 'document';
    if (fileType.startsWith('image/')) return 'image';
    if (fileType.startsWith('video/')) return 'video';
    if (fileType.startsWith('audio/')) return 'audio';
    if (fileType === 'application/pdf') return 'pdf';
    return 'document';
  };

  const getAttachmentUrl = (att) => {
    return att?.url || att?.file_url || att?.media_url || att?.path || att?.full_url || att?.download_url || '';
  };


  const formatFileSize = (bytes) => {
    if (bytes === 0) {
      return t('receptionOutpatients.attachments.fileSize.zero');
    }
    if (!bytes) {
      return t('receptionOutpatients.attachments.metadata.unknown');
    }
    const k = 1024;
    const units = ['bytes', 'kb', 'mb', 'gb'];
    const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(k)));
    const size = parseFloat((bytes / Math.pow(k, i)).toFixed(2));
    return t('receptionOutpatients.attachments.fileSize.value', {
      size,
      unit: t(`receptionOutpatients.attachments.fileSize.units.${units[i]}`)
    });
  };

  const formatDate = (value) => {
    if (!value) {
      return t('receptionOutpatients.attachments.metadata.unknown');
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return t('receptionOutpatients.attachments.metadata.unknown');
    }
    try {
      return date.toLocaleDateString(i18n.language, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        calendar: 'gregory'
      });
    } catch (err) {
      return t('receptionOutpatients.attachments.metadata.unknown');
    }
  };

  const handleClose = () => {
    setAttachments([]);
    pendingFiles.forEach(p => p.previewUrl && URL.revokeObjectURL(p.previewUrl));
    setPendingFiles([]);
    setErrors({});
    setLoadingExisting(false);
    setUploading(false);
    setDragActive(false);
    onClose();
  };

  if (!isOpen) return null;

  const modalContent = (
    <div
      className={`attachments-modal ${isOpen ? 'modal-open' : ''}`}
      dir={i18n.dir()}
      lang={i18n.language}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'grid',
        placeItems: 'center',
        zIndex: 10000,
        padding: '16px',
        fontFamily: '"Red Hat Display", sans-serif'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !loading && !loadingExisting && !uploading) {
          handleClose();
        }
      }}
    >
      <div
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '20px',
          padding: '32px',
          maxWidth: 'min(900px, 90vw)',
          width: 'min(900px, 90vw)',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: isOpen
            ? '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 25px 50px -12px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
            : '0 0 0 0 rgba(0, 0, 0, 0)',
          border: isOpen ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid transparent',
          transform: isOpen
            ? 'scale(1) translateY(0px)'
            : 'scale(0.95) translateY(20px)',
          opacity: isOpen ? 1 : 0,
          transition: isOpen
            ? 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
            : 'all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          transformOrigin: 'center center'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="modal-header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '32px',
          paddingBottom: '16px',
          borderBottom: '1px solid #E5E7EB',
          flexDirection: isRTL ? 'row-reverse' : 'row'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
            <div style={{
              width: '40px',
              height: '40px',
              backgroundColor: '#EBF8FF',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14,2 14,8 20,8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <h2 style={{
                fontSize: '24px',
                fontWeight: 600,
                color: '#1F2937',
                margin: 0,
                textAlign: isRTL ? 'right' : 'left'
              }}>
                {loadingExisting ? t('receptionOutpatients.attachments.header.loadingTitle') : t('receptionOutpatients.attachments.header.title')}
              </h2>
              {loadingExisting && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginTop: '4px'
                }}>
                  <div style={{
                    width: '12px',
                    height: '12px',
                    border: '2px solid #2563EB',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  <span style={{
                    fontSize: '12px',
                    color: '#6B7280',
                    textAlign: isRTL ? 'right' : 'left'
                  }}>
                    {t('receptionOutpatients.attachments.header.loadingExisting')}
                  </span>
                </div>
              )}
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={loading || loadingExisting || uploading}
            aria-label={t('receptionOutpatients.attachments.buttons.close')}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: (loading || loadingExisting || uploading) ? 'not-allowed' : 'pointer',
              color: '#6B7280',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (!loading && !loadingExisting && !uploading) {
                e.target.style.backgroundColor = '#F3F4F6';
                e.target.style.color = '#374151';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading && !loadingExisting && !uploading) {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#6B7280';
              }
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18" />
              <path d="M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable Content Wrapper */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingRight: '8px' }}>
          {/* Upload Area - Hidden when read-only */}
          {!readOnly && (
            <div className="attachments-section" style={{
              backgroundColor: '#F8FAFC',
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '24px',
              border: `2px dashed ${dragActive ? '#2563EB' : '#D1D5DB'}`,
              transition: 'all 0.2s ease'
            }}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                backgroundColor: dragActive ? '#EBF8FF' : '#F3F4F6',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '16px',
                transition: 'all 0.2s ease'
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={dragActive ? '#2563EB' : '#6B7280'} strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7,10 12,15 17,10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
              </div>
              <h3 style={{
                fontSize: '18px',
                fontWeight: 600,
                color: '#1F2937',
                margin: '0 0 8px 0',
                textAlign: isRTL ? 'right' : 'center'
              }}>
                {dragActive ? t('receptionOutpatients.attachments.upload.dropActive') : t('receptionOutpatients.attachments.upload.title')}
              </h3>
              <p style={{
                fontSize: '14px',
                color: '#6B7280',
                margin: '0 0 16px 0',
                textAlign: isRTL ? 'right' : 'center'
              }}>
                {t('receptionOutpatients.attachments.upload.subtitle')}
              </p>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                style={{
                  padding: '10px 20px',
                  backgroundColor: uploading ? '#93C5FD' : '#2563EB',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                {uploading && (
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid #FFFFFF',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                )}
                {uploading ? t('receptionOutpatients.attachments.upload.buttons.uploading') : t('receptionOutpatients.attachments.upload.buttons.choose')}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => e.target.files && handleFiles(e.target.files)}
              />
              <p style={{
                fontSize: '12px',
                color: '#9CA3AF',
                margin: '8px 0 0 0'
              }}>
                {t('receptionOutpatients.attachments.upload.supported', { maxSize: maxFileSizeMB })}
              </p>
            </div>
          </div>
          )}

          {/* Pending Previews - Hidden when read-only */}
          {!readOnly && pendingFiles.length > 0 && (
            <div style={{
              backgroundColor: '#FFFFFF',
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#1F2937', textAlign: isRTL ? 'right' : 'left' }}>
                  {t('receptionOutpatients.attachments.pending.title', { count: pendingFiles.length })}
                </h4>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => uploadFiles(pendingFiles)}
                    disabled={uploading}
                    style={{
                      padding: '8px 14px',
                      backgroundColor: uploading ? '#93C5FD' : '#2563EB',
                      color: '#FFFFFF',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: 500,
                      cursor: uploading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {uploading ? t('receptionOutpatients.attachments.upload.buttons.uploading') : t('receptionOutpatients.attachments.pending.uploadAll')}
                  </button>
                  <button
                    onClick={() => {
                      pendingFiles.forEach(p => p.previewUrl && URL.revokeObjectURL(p.previewUrl));
                      setPendingFiles([]);
                    }}
                    disabled={uploading}
                    style={{
                      padding: '8px 14px',
                      backgroundColor: 'transparent',
                      color: '#374151',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: 500,
                      cursor: uploading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {t('receptionOutpatients.attachments.pending.clear')}
                  </button>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px' }}>
                {pendingFiles.map((item, idx) => (
                  <div key={idx} style={{ border: '1px solid #E5E7EB', borderRadius: '10px', padding: '10px' }}>
                    <div style={{ height: '140px', borderRadius: '8px', backgroundColor: '#F8FAFC', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', marginBottom: '8px' }}>
                      {item.kind === 'image' && (
                        <img src={item.previewUrl} alt={item.file.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                      )}
                      {item.kind === 'video' && (
                        <video src={item.previewUrl} style={{ maxWidth: '100%', maxHeight: '100%' }} controls muted />
                      )}
                      {item.kind !== 'image' && item.kind !== 'video' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#6B7280' }}>
                          {getFileIcon(item.file.type || '')}
                          <span style={{ fontSize: '12px' }}>
                            {item.file.type || t('receptionOutpatients.attachments.metadata.document')}
                          </span>
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: isRTL ? 'right' : 'left' }}>
                          {item.file.file_name || item.file.name || t('receptionOutpatients.attachments.fallback.pending', { number: idx + 1 })}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6B7280' }}>{formatFileSize(item.file.size)}</div>
                      </div>
                      <button
                        onClick={() => removePendingFile(idx)}
                        disabled={uploading}
                        aria-label={t('receptionOutpatients.attachments.actions.remove')}
                        style={{
                          width: '28px', height: '28px', borderRadius: '6px', border: 'none', backgroundColor: 'transparent', color: '#EF4444', cursor: uploading ? 'not-allowed' : 'pointer'
                        }}
                        title={t('receptionOutpatients.attachments.actions.remove')}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M18 6L6 18" />
                          <path d="M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Attachments List */}
          {loadingExisting ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px'
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                border: '3px solid #F3F3F3',
                borderTop: '3px solid #2563EB',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <p style={{ marginTop: '16px', color: '#6B7280', textAlign: isRTL ? 'right' : 'center' }}>
                {t('receptionOutpatients.attachments.loading')}
              </p>
            </div>
          ) : attachments.length === 0 ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px',
              textAlign: 'center'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                backgroundColor: '#F3F4F6',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '16px'
              }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14,2 14,8 20,8" />
                </svg>
              </div>
              <h3 style={{
                fontSize: '18px',
                fontWeight: 600,
                color: '#6B7280',
                margin: '0 0 8px 0',
                textAlign: isRTL ? 'right' : 'center'
              }}>
                {t('receptionOutpatients.attachments.empty.title')}
              </h3>
              <p style={{
                fontSize: '14px',
                color: '#9CA3AF',
                margin: 0,
                textAlign: isRTL ? 'right' : 'center'
              }}>
                {readOnly
                  ? t('receptionOutpatients.attachments.empty.readOnly')
                  : t('receptionOutpatients.attachments.empty.description')}
              </p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '16px'
            }}>
              {attachments.map((attachment, index) => {
                const attachmentUrl = getAttachmentUrl(attachment);
                const attachmentKind = getAttachmentKind(attachment.file_type || attachment.mime_type);
                const isMedia = attachmentKind === 'image' || attachmentKind === 'video';

                return (
                  <div
                    key={attachment.id || index}
                    style={{
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #E5E7EB',
                      borderRadius: '12px',
                      padding: '16px',
                      transition: 'all 0.2s ease',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.borderColor = '#2563EB';
                      e.target.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.borderColor = '#E5E7EB';
                      e.target.style.boxShadow = 'none';
                    }}
                    onClick={() => {
                      if (attachmentUrl) {
                        window.open(attachmentUrl, '_blank');
                      }
                    }}
                  >
                    {/* Media Preview Section */}
                    {isMedia && attachmentUrl && (
                      <div style={{
                        height: '160px',
                        borderRadius: '8px',
                        backgroundColor: '#F8FAFC',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        marginBottom: '12px',
                        border: '1px solid #E5E7EB'
                      }}>
                        {attachmentKind === 'image' && (
                          <img
                            src={attachmentUrl}
                            alt={attachment.original_name || attachment.filename}
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              objectFit: 'contain',
                              borderRadius: '6px'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        )}
                        {attachmentKind === 'video' && (
                          <video
                            src={attachmentUrl}
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              borderRadius: '6px'
                            }}
                            controls
                            muted
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        )}
                        {/* Fallback for failed media loads */}
                        <div style={{
                          display: 'none',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#6B7280',
                          gap: '8px'
                        }}>
                          {getFileIcon(attachment.file_type || attachment.mime_type || '')}
                          <span style={{ fontSize: '12px' }}>{t('receptionOutpatients.attachments.preview.unavailable')}</span>
                        </div>
                      </div>
                    )}

                    {/* File Info Section */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '12px'
                    }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        backgroundColor: '#F3F4F6',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#6B7280'
                      }}>
                        {getFileIcon(attachment.file_type || attachment.mime_type)}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h4 style={{
                          fontSize: '14px',
                          fontWeight: 600,
                          color: '#1F2937',
                          margin: '0 0 4px 0',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {attachment.file_name
                            || attachment.filename
                            || attachment.original_name
                            || t('receptionOutpatients.attachments.fallback.attachment', { number: index + 1 })}
                        </h4>
                        <p style={{
                          fontSize: '12px',
                          color: '#6B7280',
                          margin: 0
                        }}>
                          {formatFileSize(attachment.file_size)}
                        </p>
                      </div>
                      {!readOnly && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteAttachment(attachment.id);
                          }}
                          disabled={loading}
                          aria-label={t('receptionOutpatients.attachments.actions.delete')}
                          style={{
                            width: '28px',
                            height: '28px',
                            borderRadius: '6px',
                            border: 'none',
                            backgroundColor: 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            color: '#EF4444',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (!loading) {
                              e.target.style.backgroundColor = '#FEF2F2';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                          }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M3 6h18" />
                            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                          </svg>
                        </button>
                      )}
                    </div>

                    {/* Metadata Section */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '12px',
                      color: '#6B7280',
                      flexDirection: isRTL ? 'row-reverse' : 'row'
                    }}>
                          <span>{formatDate(attachment.created_at)}</span>
                      <span>•</span>
                          <span>{attachment.file_type || attachment.mime_type || t('receptionOutpatients.attachments.metadata.document')}</span>
                      {isMedia && (
                        <>
                          <span>•</span>
                          <span style={{
                            color: '#2563EB',
                            fontWeight: 500,
                                cursor: 'pointer'
                          }}>
                                {t('receptionOutpatients.attachments.preview.open')}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="action-buttons" style={{
          display: 'flex',
          gap: '12px',
          justifyContent: isRTL ? 'flex-start' : 'flex-end',
          paddingTop: '24px',
          borderTop: '1px solid #E5E7EB',
          flexDirection: isRTL ? 'row-reverse' : 'row'
        }}>
          <button
            onClick={handleClose}
            disabled={loading || loadingExisting || uploading}
            style={{
              padding: '12px 24px',
              backgroundColor: 'transparent',
              color: (loading || loadingExisting || uploading) ? '#9CA3AF' : '#374151',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: (loading || loadingExisting || uploading) ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              fontFamily: '"Red Hat Display", sans-serif',
              minWidth: '80px'
            }}
            onMouseEnter={(e) => {
              if (!loading && !loadingExisting && !uploading) {
                e.target.style.backgroundColor = '#F9FAFB';
                e.target.style.borderColor = '#9CA3AF';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading && !loadingExisting && !uploading) {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.borderColor = '#D1D5DB';
              }
            }}
          >
            {t('receptionOutpatients.attachments.buttons.close')}
          </button>
        </div>

        {/* Add modern animations and responsive styles */}
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }

            @keyframes fadeInUp {
              from {
                opacity: 0;
                transform: translateY(20px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }

            @keyframes slideInFromBottom {
              from {
                opacity: 0;
                transform: translateY(20px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }

            @keyframes fadeIn {
              from {
                opacity: 0;
              }
              to {
                opacity: 1;
              }
            }

            /* Modern scrollbar styling */
            ::-webkit-scrollbar {
              width: 8px;
              height: 8px;
            }

            ::-webkit-scrollbar-track {
              background: #f1f5f9;
              border-radius: 0 8px 8px 0;
            }

            ::-webkit-scrollbar-thumb {
              background: #cbd5e1;
              border-radius: 0 8px 8px 0;
              transition: background 0.2s ease;
            }

            ::-webkit-scrollbar-thumb:hover {
              background: #94a3b8;
            }

            ::-webkit-scrollbar-corner {
              background: #f1f5f9;
              border-radius: 0 0 8px 0;
            }

            * {
              scrollbar-width: thin;
              scrollbar-color: #cbd5e1 #f1f5f9;
            }

            /* Animation classes for all elements - formal timing */
            .attachments-section,
            .action-buttons,
            .modal-header {
              animation-delay: 0.1s;
              opacity: 0;
            }

            .attachments-section {
              animation: slideInFromBottom 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
            }

            .action-buttons {
              animation: fadeIn 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
            }

            .modal-header {
              animation: fadeInUp 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
            }

            /* Perfect centering for all screen sizes */
            .attachments-modal {
              display: grid !important;
              place-items: center !important;
              min-height: 100vh !important;
              min-width: 100vw !important;
            }

            /* Mobile Responsiveness */
            @media (max-width: 768px) {
              .attachments-modal {
                padding: 8px !important;
              }

              .attachments-modal > div {
                max-width: calc(100vw - 16px) !important;
                max-height: calc(100vh - 16px) !important;
                width: calc(100vw - 16px) !important;
                padding: 20px !important;
              }

              .attachments-grid {
                grid-template-columns: 1fr !important;
                gap: 12px !important;
              }

              .upload-area {
                padding: 16px !important;
              }
            }

            @media (max-width: 480px) {
              .attachments-modal-overlay > div {
                padding: 16px !important;
                margin: 5px !important;
              }

              .modal-header h2 {
                font-size: 20px !important;
              }
            }
          `}
        </style>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default AttachmentsModal;
